import { useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { getRoleCode } from "constant/Role"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDNumberInput from "../MDNumberInput"
import MDSelect from "components/MDSelect"
import MDTypography from "components/MDTypography"
import { useGetPurchasesQuery } from "shared/redux/endpoints/procurementPurchases"
import { formatVND } from "../../constants"

// A6 — merge another payable Purchase into an existing (Draft) Payment
// Request. Offers every payable PO not already linked; the manager picks one
// and how much of it to allocate.
const LinkPurchaseModal = ({ paymentRequestId, excludePurchaseIds = [], busy, onCancel, onSubmit }) => {
  const activeRoleCode = useSelector((state) => getRoleCode(state.auth?.role))
  const { data, isFetching } = useGetPurchasesQuery({ payable: true, size: 200, role: activeRoleCode })
  const [purchaseId, setPurchaseId] = useState(null)
  const [allocatedAmount, setAllocatedAmount] = useState("")

  const options = useMemo(
    () =>
      (data?.items || [])
        .filter((p) => !excludePurchaseIds.includes(p.id))
        .map((p) => ({
          value: p.id,
          label: `${p.poCode} · ${p.vendorName || p.supplierName} · remaining ${formatVND(p.remainingToPay)}`,
          remainingToPay: p.remainingToPay,
        })),
    [data, excludePurchaseIds]
  )
  const selected = options.find((o) => o.value === purchaseId)

  // Allocating more than what's left on the PO is a warning, not a block —
  // the backend no longer 409s for it (2026-09 barrier-removal follow-up).
  const invalid = !purchaseId || !(Number(allocatedAmount) > 0)
  const overAllocated = selected && Number(allocatedAmount) > selected.remainingToPay

  return (
    <Dialog open onClose={() => !busy && onCancel()} fullWidth maxWidth="sm">
      <DialogTitle>Add another PO to this payment request</DialogTitle>
      <DialogContent>
        <MDBox pt={1} display="flex" flexDirection="column" gap={2}>
          <MDSelect
            required
            labelType="non-sticky"
            labelText="Purchase order"
            options={options}
            value={selected || null}
            onChange={(o) => setPurchaseId(o?.value ?? null)}
            isDisabled={busy || isFetching}
            isLoading={isFetching}
          />
          <MDNumberInput
            fullWidth
            required
            labelType="non-sticky"
            labelText="Allocated amount (VND)"
            value={allocatedAmount}
            onChange={setAllocatedAmount}
            disabled={busy || !purchaseId}
          />
          {selected && (
            <MDTypography variant="caption" color={overAllocated ? "error" : "text"}>
              Remaining on this PO: {formatVND(selected.remainingToPay)}
              {overAllocated && " — this will put the PO over its budget."}
            </MDTypography>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onCancel} disabled={busy}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={busy || invalid}
          onClick={() => onSubmit({ purchaseId, allocatedAmount: Number(allocatedAmount) })}
        >
          {busy ? "Adding…" : "Add"}
        </MDButton>
      </DialogActions>
    </Dialog>
  )
}

export default LinkPurchaseModal
